/**
 * BooksCloseGate — the Monday lockout for closing the books (Matt, 2026-10-28). docs/BOOKKEEPING.md
 * "The monthly close".
 *
 * Lands on whoever holds Bookkeeping (the finance-admin module) from the due Monday (the first Monday
 * on or after the 4th). "Not today" once a day Monday to Thursday, none on Friday; after Friday it is
 * there every morning until the month is closed. The server decides all of that (closeGate); this
 * only shows it. Fails open: no Bookkeeping access, or any error, means no lockout.
 *
 * Waits behind the Time Gate and thread triage, like the Progress Gate, and never shows where
 * docs/LOCKOUTS.md says lockouts must not (it is mounted only by OutlineView and LockoutHost).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { getCloseGate, snoozeCloseGate } from '../services/financeImport.js'
import { lockScroll } from '../outline/scrollLock.js'
import TriageTiles from './TriageTiles.jsx'

const longDate = (iso) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }) : ''

export default function BooksCloseGate() {
  const [gate, setGate] = useState(null)
  const [ready, setReady] = useState(false)
  const [timeGateBlocking, setTimeGateBlocking] = useState(false)
  const [triageBlocking, setTriageBlocking] = useState(false)
  const [opened, setOpened] = useState(false)
  const pollRef = useRef(null)

  const recheck = useCallback(async () => {
    if (!isSeatLoaded() || !(ctx.currentMember && ctx.currentMember.id)) return
    if (!hasModule('finance-admin')) { setGate(null); return }
    try { setGate(await getCloseGate()) } catch { setGate(null) }
  }, [])

  useEffect(() => {
    if (ready) return
    let tries = 0
    pollRef.current = setInterval(() => {
      tries++
      if (isSeatLoaded() && ctx.currentMember && ctx.currentMember.id) {
        clearInterval(pollRef.current); pollRef.current = null
        setReady(true); recheck()
      } else if (tries > 40) { clearInterval(pollRef.current); pollRef.current = null }
    }, 400)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [ready, recheck])

  useEffect(() => {
    const onFocus = () => recheck()
    const onTime = (e) => setTimeGateBlocking(!!(e.detail && e.detail.blocking))
    const onTriage = (e) => setTriageBlocking(!!(e.detail && e.detail.blocking))
    window.addEventListener('focus', onFocus)
    window.addEventListener('time-gate-blocking-changed', onTime)
    window.addEventListener('triage-gate-blocking-changed', onTriage)
    window.addEventListener('books-closed', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('time-gate-blocking-changed', onTime)
      window.removeEventListener('triage-gate-blocking-changed', onTriage)
      window.removeEventListener('books-closed', onFocus)
    }
  }, [recheck])

  // Opening the close is the answer for this sitting; the lockout returns on the next check if the
  // month is still open (a reload, a new morning).
  const blocking = !isEmbedded() && !!(gate && gate.show) && !opened && !timeGateBlocking && !triageBlocking
  useEffect(() => { if (blocking) return lockScroll() }, [blocking])
  if (!blocking) return null

  const month = gate.month.split(' ')[0]
  const options = [
    { key: 'close', icon: 'did', label: 'Close it now', sub: 'Opens the close, starting with the downloads.', tone: 'primary',
      open: () => { setOpened(true); window.dispatchEvent(new CustomEvent('open-finance')) } },
    { key: 'snooze', icon: 'snooze', label: 'Not today', tone: 'caution',
      sub: gate.canSnooze ? 'Back tomorrow morning. Once a day through Thursday.' : 'Not on Friday, or once a day already used.',
      disabled: !gate.canSnooze,
      step: { title: 'Not today', body: `The reminder comes back tomorrow morning. ${month} has to be closed by ${longDate(gate.friday)}; after that there is no "Not today".`,
        kind: 'confirm', confirm: () => 'Not today' } },
  ]
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(7,39,45,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-label={`Close ${gate.month}`} style={{ width: 'min(620px, 100%)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', borderRadius: 12, padding: '32px 36px', fontFamily: '"DM Sans", sans-serif', boxSizing: 'border-box' }}>
        <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' }}>{longDate(gate.today)}</div>
        <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.2, marginTop: 10 }}>{month}'s books need closing</div>
        <div style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--aj-text-mid)', marginTop: 10 }}>
          {gate.today > gate.friday
            ? `It was due ${longDate(gate.due)}. This stays every morning until ${month} is closed.`
            : `Closing ${month} takes your downloads, a few decisions and one button. It has to be done by ${longDate(gate.friday)}.`}
        </div>
        <TriageTiles options={options} onConfirm={async (key) => {
          if (key === 'snooze') { await snoozeCloseGate(); setGate(g => ({ ...g, show: false })); return 'Back tomorrow morning.' }
          return ''
        }} />
      </div>
    </div>
  )
}
